/**
 * Capital Analytics Tab — non-dividend stocks only.
 *
 * Mirrors the Dividend Triangle tab structure but evaluates non-div
 * names via the 4-factor Equity Quality Rank model (Shareholder Yield,
 * CFROI, Operating Momentum, Forensic Safety). Data is pre-computed
 * by the weekly Sunday cron and cached on `stock_cache.equity_quality`.
 *
 * NO LLM is called when the tab opens — only when the user clicks the
 * "Write your thesis" button at the bottom.
 *
 * Spec: /app/memory/CAPITAL_REINVEST_TAB_SPEC.md
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import TapTooltipChart from '@/components/TapTooltipChart';
import {
  ShieldCheck, ShieldAlert, ShieldX, TrendingUp, TrendingDown, Minus,
  Sparkles, AlertTriangle, CheckCircle2, XCircle, Loader2, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import AbbrTip from '@/components/AbbrTip';
import MinerviniTemplateCard from '@/components/MinerviniTemplateCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPct = (v, dec = 1) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return `${(v * 100).toFixed(dec)}%`;
};
const fmtNum = (v, dec = 2) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Number(v).toFixed(dec);
};
const fmtBigCash = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  return `${v.toFixed(0)}`;
};

const verdictPalette = {
  BUY:   { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', ring: 'ring-emerald-500/40', icon: TrendingUp },
  WATCH: { bg: 'bg-amber-500/10',   text: 'text-amber-700 dark:text-amber-400',   ring: 'ring-amber-500/40',   icon: Minus },
  AVOID: { bg: 'bg-rose-500/10',    text: 'text-rose-700 dark:text-rose-400',     ring: 'ring-rose-500/40',    icon: TrendingDown },
};

const zonePalette = {
  Safe:     { text: 'text-emerald-700 dark:text-emerald-400', icon: ShieldCheck },
  Grey:     { text: 'text-amber-700 dark:text-amber-400',     icon: ShieldAlert },
  Distress: { text: 'text-rose-700 dark:text-rose-400',       icon: ShieldX },
  Unknown:  { text: 'text-muted-foreground',                   icon: ShieldAlert },
};

export default function CapitalAnalyticsTab({ ticker, stockName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/stocks/${ticker}/equity-quality`);
      setData(res.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="capital-analytics-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading Capital Analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-rose-600 dark:text-rose-400" data-testid="capital-analytics-error">
        {error}
        <MinerviniTemplateCard ticker={ticker} />
      </div>
    );
  }

  if (!data?.available || !data?.equity_quality?.rank) {
    return (
      <div className="p-6" data-testid="capital-analytics-empty">
        <Card className="p-6 text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-amber-600 dark:text-amber-400 mb-3" />
          <div className="text-sm font-semibold mb-1">Quality model not scored yet</div>
          <p className="text-xs text-muted-foreground">
            The Capital Analytics engine runs every Sunday at 02:00 UTC.
            This ticker may be missing required Massive financial filings
            or hasn&apos;t been rescored since being marked non-dividend.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Tab eq={data.equity_quality} ticker={ticker} stockName={stockName} />
      <MinerviniTemplateCard ticker={ticker} />
    </>
  );
}


function Tab({ eq, ticker, stockName }) {
  const verdict = eq.verdict || 'WATCH';
  const v = verdictPalette[verdict] || verdictPalette.WATCH;
  const VerdictIcon = v.icon;
  const zone = eq.altman_zone || 'Unknown';
  const zoneStyle = zonePalette[zone] || zonePalette.Unknown;
  const ZoneIcon = zoneStyle.icon;

  // Round 97h2 (Feb 27, 2026) — Radar percentile anchoring vs sector
  // peers. Previously each spoke was scaled with hardcoded
  // multipliers (×2, ×2.5, ×10) which distorted the visual: a 5%
  // CAGR could look like a maxed spike even though it was just
  // average. We now convert the persisted SECTOR-NEUTRALISED
  // z-scores into a 0-100 percentile via a normal-CDF approximation
  // (Abramowitz & Stegun 26.2.17). 50 = median peer. 90 = top-decile.
  // Tooltip still shows the raw percent so users can verify.
  function normalCdfPct(z) {
    if (!Number.isFinite(z)) return 50;
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989422804 * Math.exp(-z * z / 2);
    let p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    if (z > 0) p = 1 - p;
    return Math.round(Math.max(1, Math.min(99, p * 100)));
  }
  const radarData = useMemo(() => {
    const revPct = (eq.revenue_cagr_3y || 0) * 100;
    const roicPct = (eq.roic || 0) * 100;
    const byPct = (eq.buyback_yield_raw || 0) * 100;
    // The EQR cron persists z-scores for buyback yield and cfroi but
    // not revenue/ROIC directly. For the radar we approximate the
    // sector percentile by using the closest z-score we DO have, or
    // fall back to a clamped raw-value scale when the z isn't on
    // the payload yet (e.g. cron hasn't re-run after a schema bump).
    const revZ = eq.z_cfroi;  // closest growth proxy on the payload
    const roicZ = eq.z_cfroi;
    const byZ = eq.z_buyback_yield_adjusted;
    const revPercentile = Number.isFinite(revZ)
      ? normalCdfPct(revZ)
      : Math.max(0, Math.min(100, revPct * 2));
    const roicPercentile = Number.isFinite(roicZ)
      ? normalCdfPct(roicZ)
      : Math.max(0, Math.min(100, roicPct * 2.5));
    const byPercentile = Number.isFinite(byZ)
      ? normalCdfPct(byZ)
      : Math.max(0, Math.min(100, (byPct + 5) * 10));
    return [
      { axis: 'Revenue CAGR',  value: revPercentile,  raw: `${revPct.toFixed(1)}% (P${revPercentile})` },
      { axis: 'ROIC',          value: roicPercentile, raw: `${roicPct.toFixed(1)}% (P${roicPercentile})` },
      { axis: 'Shareholder Y', value: byPercentile,   raw: `${byPct.toFixed(2)}% (P${byPercentile})` },
    ];
  }, [eq]);

  const checklist = eq.checklist || {};

  return (
    <div className="p-3 sm:p-4 space-y-4" data-testid="capital-analytics-tab">
      {/* Section 1 — Score header */}
      <Card className={`p-4 ${v.bg} ring-1 ${v.ring}`} data-testid="capital-score-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Capital Reinvestment Score
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="text-4xl font-bold tabular-nums" data-testid="capital-score-value">
                {Math.round(eq.rank)}
              </div>
              <div className="text-sm text-muted-foreground">/ 100</div>
              <WowDeltaPill currentRank={eq.rank} previousRank={eq.previous_rank} />
            </div>
            <div className={`text-sm font-semibold mt-1 ${v.text}`}>
              {eq.verdict_label}
            </div>
          </div>
          <Badge
            className={`${v.bg} ${v.text} border-0 px-3 py-1.5 text-sm font-bold gap-1.5`}
            data-testid={`capital-verdict-${verdict.toLowerCase()}`}
          >
            <VerdictIcon className="w-4 h-4" /> {verdict}
          </Badge>
        </div>
        <WhyBullets eq={eq} sector={eq.sector} />
        <RankBreakdown eq={eq} />
      </Card>

      {/* Section 2 — Reinvest Buy Checklist */}
      <Card className="p-4" data-testid="capital-checklist-card">
        <SectionHeader title="Capital Allocator Checklist" subtitle="5-criteria pass/fail" />
        <ChecklistRow
          label="Shareholder Yield + Revenue CAGR > 12%"
          pass={checklist.yield_plus_growth_gt_12pct}
          value={`${(((eq.buyback_yield_raw || 0) + (eq.revenue_cagr_3y || 0)) * 100).toFixed(1)}%`}
        />
        <ChecklistRow
          label="CFROI > 15%"
          pass={checklist.cfroi_gt_15pct}
          value={fmtPct(eq.cfroi_raw)}
        />
        <ThresholdRangeBar
          value={eq.cfroi_raw}
          min={-0.05} max={0.30}
          threshold={0.15}
          gradeZones={[
            { start: -0.05, end: 0, color: 'red' },
            { start: 0, end: 0.12, color: 'amber' },
            { start: 0.12, end: 0.30, color: 'green' },
          ]}
          label="cfroi"
          format="pct"
        />
        <ChecklistRow
          label="Dilution restrained (< 1.5% YoY)"
          pass={checklist.dilution_under_1_5pct}
          value={eq.share_float_yoy_pct !== null ? `${eq.share_float_yoy_pct.toFixed(1)}%` : '—'}
        />
        <ChecklistRow
          label="Operating Momentum positive"
          pass={checklist.momentum_positive}
          value={fmtNum(eq.operating_momentum_raw)}
        />
        <ChecklistRow
          label="Altman Z above distress (> 1.23)"
          pass={checklist.altman_above_distress}
          value={fmtNum(eq.altman_z_raw)}
        />
        <div className="mt-3 pt-3 border-t border-border/50 text-sm font-medium" data-testid="capital-checklist-summary">
          Passes <span className="text-primary font-bold">{checklist.passed_count}</span> of {checklist.total}
          {checklist.passed_count >= 4 && <span className="text-emerald-600 dark:text-emerald-400 ml-2">— Strong capital allocator</span>}
          {checklist.passed_count === 3 && <span className="text-amber-600 dark:text-amber-400 ml-2">— Mixed signal</span>}
          {checklist.passed_count <= 2 && <span className="text-rose-600 dark:text-rose-400 ml-2">— Below threshold</span>}
        </div>
      </Card>

      {/* Section 3 — Our Take + What Changes Our Mind */}
      <Card className="p-4" data-testid="capital-opinion-card">
        <SectionHeader title="Our Take" />
        <p className="text-sm leading-relaxed">{verdictCopy(verdict, eq)}</p>
        <div className="mt-4 pt-3 border-t border-border/50">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
            What changes our mind
          </div>
          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>• Net Shareholder Yield turns negative (dilution outstrips buybacks)</li>
            <li>• Modified Altman Z drops below 1.23 (distress zone)</li>
            <li>• Beneish M-Score crosses above -1.78 (manipulation flag)</li>
          </ul>
        </div>
      </Card>

      {/* Section 4 — Capital Reinvestment Triangle (radar) */}
      <Card className="p-4" data-testid="capital-radar-card">
        <SectionHeader title="Capital Reinvestment Triangle" subtitle="Sector-relative percentile (50 = median peer)" />
        <div className="h-[260px] mt-2">
          <TapTooltipChart className="h-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} cx="50%" cy="52%" outerRadius="72%">
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }} />
              <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
              <Radar
                name="Quality"
                dataKey="value"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary))"
                fillOpacity={0.35}
              />
            </RadarChart>
          </ResponsiveContainer>
          </TapTooltipChart>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs mt-2">
          {radarData.map((d) => (
            <div key={d.axis}>
              <div className="text-muted-foreground">{d.axis}</div>
              <div className="font-semibold tabular-nums">{d.raw}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Section 5 — Forensic Safety & Momentum */}
      <Card className="p-4" data-testid="capital-forensic-card">
        <SectionHeader title="Forensic Safety & Momentum" />
        <MetricRow
          label={<AbbrTip term="ALTMAN_Z">Modified Altman Z</AbbrTip>}
          value={fmtNum(eq.altman_z_raw)}
          threshold="> 1.23"
          status={zone}
          icon={<ZoneIcon className={`w-4 h-4 ${zoneStyle.text}`} />}
        />
        <ThresholdRangeBar
          value={eq.altman_z_raw}
          min={0} max={20}
          threshold={1.23}
          gradeZones={[
            { start: 0, end: 1.23, color: 'red' },
            { start: 1.23, end: 2.99, color: 'amber' },
            { start: 2.99, end: 20, color: 'green' },
          ]}
          label="altman"
          format="num"
        />
        <MetricRow
          label={<AbbrTip term="BENEISH_M">Beneish M-Score</AbbrTip>}
          value={fmtNum(eq.beneish_m_raw)}
          threshold="< -1.78"
          status={eq.accounting_risk_flag ? 'FLAG' : 'CLEAN'}
        />
        <ThresholdRangeBar
          value={eq.beneish_m_raw}
          min={-3} max={0}
          threshold={-1.78}
          gradeZones={[
            { start: -3, end: -2.22, color: 'green' },
            { start: -2.22, end: -1.78, color: 'amber' },
            { start: -1.78, end: 0, color: 'red' },
          ]}
          label="beneish"
          format="num"
          invert
        />
        <MetricRow
          label={<AbbrTip term="OPERATING_MOMENTUM">Operating Momentum</AbbrTip>}
          value={fmtNum(eq.operating_momentum_raw)}
          threshold="> 0"
          status={(eq.operating_momentum_raw || 0) > 0 ? 'EXPANDING' : 'CONTRACTING'}
        />
        <ThresholdRangeBar
          value={eq.operating_momentum_raw}
          min={-2} max={2}
          threshold={0}
          gradeZones={[
            { start: -2, end: 0, color: 'red' },
            { start: 0, end: 0.5, color: 'amber' },
            { start: 0.5, end: 2, color: 'green' },
          ]}
          label="opmom"
          format="num"
        />
        <MetricRow
          label={<AbbrTip term="SHARES_OUTSTANDING">Share Float YoY</AbbrTip>}
          value={eq.share_float_yoy_pct !== null ? `${eq.share_float_yoy_pct.toFixed(1)}%` : '—'}
          threshold="≤ -1.0%"
          status={eq.true_share_float_shrinking ? 'SHRINKING' : 'EXPANDING'}
        />
      </Card>

      {/* Section 5b — Buyback Effectiveness Score (Feb 28, 2026) */}
      <BuybackEffectivenessCard ticker={ticker} />

      {/* Section 5c — Rule of 40 (Feb 28, 2026) */}
      <RuleOf40Card eq={eq} />

      {/* Section 6 — Shareholder Alignment */}
      <Card className="p-4" data-testid="capital-alignment-card">
        <SectionHeader title="Shareholder Alignment" subtitle="Alternative yield channel" />
        <div className="text-xs space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              <AbbrTip term="SHAREHOLDER_YIELD">Net Shareholder Yield</AbbrTip>
            </span>
            <span className="font-semibold tabular-nums">{fmtPct(eq.buyback_yield_raw, 2)}</span>
          </div>
          {/* Round 97h (Feb 27, 2026) — Headline-factor range bar.
              Shareholder Yield is the largest weight in the model
              (30%) so it deserves the same visual treatment as the
              forensic metrics. Scale -5% → +5%, threshold at 0 (pass
              line), elite buyback zone above +3%. */}
          <ThresholdRangeBar
            value={eq.buyback_yield_raw}
            min={-0.05} max={0.05}
            threshold={0}
            gradeZones={[
              { start: -0.05, end: -0.01, color: 'red' },
              { start: -0.01, end: 0.03, color: 'amber' },
              { start: 0.03, end: 0.05, color: 'green' },
            ]}
            label="shareholder-yield"
            format="pct"
          />
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              <AbbrTip term="ROIC">ROIC</AbbrTip> (Return on Invested Capital)
            </span>
            <span className="font-semibold tabular-nums">{fmtPct(eq.roic)}</span>
          </div>
          {/* Round 97h2 (Feb 27, 2026) — ROIC range bar. Scale -10% →
              +30% with industry-standard zones: red ≤0% (destroying
              capital), amber 0-10% (modest), green 10-20% (productive),
              elite-green ≥20% (best-in-class). Threshold at 10% (the
              hurdle rate used in the model). */}
          <ThresholdRangeBar
            value={eq.roic}
            min={-0.10} max={0.30}
            threshold={0.10}
            gradeZones={[
              { start: -0.10, end: 0, color: 'red' },
              { start: 0, end: 0.10, color: 'amber' },
              { start: 0.10, end: 0.30, color: 'green' },
            ]}
            label="roic"
            format="pct"
          />
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              <AbbrTip term="CFROI">CFROI</AbbrTip> (Cash-Flow ROI)
            </span>
            <span className="font-semibold tabular-nums">{fmtPct(eq.cfroi_raw)}</span>
          </div>
          {/* Same scale + thresholds as the Checklist CFROI bar so
              users see the metric consistently across sections. */}
          <ThresholdRangeBar
            value={eq.cfroi_raw}
            min={-0.05} max={0.30}
            threshold={0.15}
            gradeZones={[
              { start: -0.05, end: 0, color: 'red' },
              { start: 0, end: 0.12, color: 'amber' },
              { start: 0.12, end: 0.30, color: 'green' },
            ]}
            label="cfroi-alignment"
            format="pct"
          />
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              Revenue 3-yr <AbbrTip term="CAGR">CAGR</AbbrTip>
            </span>
            <span className="font-semibold tabular-nums">{fmtPct(eq.revenue_cagr_3y)}</span>
          </div>
          {/* Round 97h2 — Revenue 3yr CAGR bar. Scale -20% → +50%,
              threshold at +5% (the floor we use in the
              "Yield + CAGR > 12%" checklist combined with a typical
              0% buyback), high-growth zone above +20% in emerald. */}
          <ThresholdRangeBar
            value={eq.revenue_cagr_3y}
            min={-0.20} max={0.50}
            threshold={0.05}
            gradeZones={[
              { start: -0.20, end: 0, color: 'red' },
              { start: 0, end: 0.20, color: 'amber' },
              { start: 0.20, end: 0.50, color: 'green' },
            ]}
            label="revenue-cagr"
            format="pct"
          />
          {eq.market_cap_smoothed && (
            <div className="flex justify-between">
              <span className="text-muted-foreground"><AbbrTip term="VWAP_MARKET_CAP">5-day VWAP Market Cap</AbbrTip></span>
              <span className="font-semibold tabular-nums">${fmtBigCash(eq.market_cap_smoothed)}</span>
            </div>
          )}
          {eq.dilution_leak_trap_warning && (
            <div className="mt-3 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs flex items-start gap-2" data-testid="capital-dilution-warning">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Dilution Leak Warning</div>
                Buyback cash isn&apos;t shrinking the float — option grants absorb it.
              </div>
            </div>
          )}
          {eq.forensic_penalty_applied && (
            <div className="mt-3 p-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 text-xs flex items-start gap-2" data-testid="capital-forensic-warning">
              <ShieldX className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Forensic Gate Triggered</div>
                Composite reduced 80% — Altman distress and/or Beneish M flag detected.
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Section 7 — Write your thesis */}
      <ThesisSection ticker={ticker} stockName={stockName} />

      {/* Footer — run date */}
      <div className="text-[10px] text-muted-foreground text-center pt-2 pb-1">
        Model run: {eq.last_model_run_date || '—'} · Universe of {eq.universe_size?.toLocaleString() || '—'} non-dividend stocks · Sector: {eq.sector}
      </div>
    </div>
  );
}


function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-2 pb-2 border-b border-border/50">
      <div className="text-sm font-semibold">{title}</div>
      {subtitle && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{subtitle}</div>}
    </div>
  );
}


function WhyBullets({ eq, sector }) {
  const bullets = [];
  const cfroi = (eq.cfroi_raw || 0) * 100;
  const by = (eq.buyback_yield_raw || 0) * 100;
  const z = eq.altman_z_raw || 0;

  if (cfroi > 15) bullets.push(`CFROI of ${cfroi.toFixed(1)}% — exceptional cash generation vs ${sector || 'sector'} peers.`);
  else if (cfroi > 0) bullets.push(`CFROI of ${cfroi.toFixed(1)}% — modest cash-on-invested-capital return.`);
  else if (cfroi < 0) bullets.push(`CFROI of ${cfroi.toFixed(1)}% — currently burning cash on operations.`);

  // Round 97e (Feb 27, 2026) — Net Shareholder Yield always rendered
  // with 2 decimals (matches the Shareholder Alignment row above so
  // users never see "-0.85%" in one place and "-0.9%" in another).
  if (by > 2) bullets.push(`Net Shareholder Yield +${by.toFixed(2)}% — actively returning capital via buybacks.`);
  else if (by < -1) bullets.push(`Net Shareholder Yield ${by.toFixed(2)}% — equity dilution outpacing buybacks.`);
  else bullets.push(`Net Shareholder Yield ${by.toFixed(2)}% — flat capital-return channel.`);

  if (z > 2.9) bullets.push(`Modified Altman Z of ${z.toFixed(1)} — balance sheet firmly in the Safe zone.`);
  else if (z >= 1.23) bullets.push(`Modified Altman Z of ${z.toFixed(1)} — Grey zone, monitor structural health.`);
  else bullets.push(`Modified Altman Z of ${z.toFixed(1)} — Distress zone, elevated bankruptcy risk.`);

  if (!eq.accounting_risk_flag) bullets.push('No Beneish M-Score anomalies detected.');
  else bullets.push('Beneish M-Score flagged — possible earnings manipulation signals.');

  return (
    <ul className="mt-3 pt-3 border-t border-border/30 space-y-1 text-xs text-muted-foreground">
      {bullets.slice(0, 4).map((b, i) => (
        <li key={i}>• {b}</li>
      ))}
    </ul>
  );
}


function ThresholdRangeBar({ value, min, max, threshold, gradeZones, label, format = 'num', invert = false }) {
  // Round 97g (Feb 27, 2026) — Compact range bar with color zones,
  // a vertical threshold marker, and a position pin for the current
  // value. Reuses the visual language of the 52-week range bar on
  // Stock Metrics but adds zone shading (red / amber / green) for
  // forensic context.
  //
  // Props:
  //   value      — current metric value
  //   min/max    — clamp scale on the bar (e.g. -3 to 0 for Beneish)
  //   threshold  — single vertical line drawn ON the bar (pass/fail)
  //   gradeZones — array of {start, end, color: 'red'|'amber'|'green'}
  //   invert     — true when "lower is better" (Beneish, D/E etc).
  //                Flips the colour of the value pin but the zone
  //                shading is already encoded by the caller.
  if (value == null || !Number.isFinite(Number(value))) return null;
  const v = Number(value);
  const clampedValue = Math.max(min, Math.min(max, v));
  const range = max - min;
  if (range <= 0) return null;
  const pct = ((clampedValue - min) / range) * 100;
  const threshPct = threshold != null ? ((Math.max(min, Math.min(max, threshold)) - min) / range) * 100 : null;

  const isInGreen = (gradeZones || []).some(z => z.color === 'green' && v >= z.start && v <= z.end);
  const isInRed = (gradeZones || []).some(z => z.color === 'red' && v >= z.start && v <= z.end);
  const pinColor = isInGreen ? 'bg-emerald-500'
    : isInRed ? 'bg-rose-500'
    : 'bg-amber-500';

  const fmtVal = (x) => {
    if (format === 'pct') return `${(x * 100).toFixed(1)}%`;
    if (format === 'pct_raw') return `${x.toFixed(1)}%`;
    return x.toFixed(2);
  };

  return (
    <div className="mt-1.5 mb-1" data-testid={`capital-bar-${label || ''}`}>
      <div className="relative h-2 rounded-full overflow-hidden bg-muted/40">
        {(gradeZones || []).map((z, i) => {
          const zStart = ((Math.max(min, z.start) - min) / range) * 100;
          const zEnd = ((Math.min(max, z.end) - min) / range) * 100;
          const w = Math.max(0, zEnd - zStart);
          const bg = z.color === 'green' ? 'bg-emerald-500/35 dark:bg-emerald-600/40'
            : z.color === 'amber' ? 'bg-amber-500/35 dark:bg-amber-600/40'
            : 'bg-rose-500/35 dark:bg-rose-600/40';
          return (
            <div
              key={i}
              className={`absolute top-0 h-full ${bg}`}
              style={{ left: `${zStart}%`, width: `${w}%` }}
            />
          );
        })}
        {threshPct != null && (
          <div
            className="absolute top-[-2px] bottom-[-2px] w-px bg-foreground/70"
            style={{ left: `${threshPct}%` }}
            title={`Threshold: ${fmtVal(threshold)}`}
          />
        )}
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-2 ring-background shadow ${pinColor}`}
          style={{ left: `calc(${pct}% - 5px)` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground/70 mt-0.5 font-mono">
        <span>{fmtVal(min)}</span>
        {threshold != null && (
          <span className="opacity-90">
            {invert ? '←' : '→'} pass at {fmtVal(threshold)}
          </span>
        )}
        <span>{fmtVal(max)}</span>
      </div>
    </div>
  );
}


function RankBreakdown({ eq }) {
  // Round 97f (Feb 27, 2026) — Math expansion under the score card.
  // Shows users exactly why their stock landed where it did:
  //   raw composite → forensic penalty → checklist penalty → final
  //   → percentile rank. Collapsed by default — power-users / paid
  //   subscribers can expand to verify the model's reasoning.
  const [open, setOpen] = useState(false);

  // Bail if the underlying numbers aren't on the cached payload yet
  // (EQR cron hasn't re-run after the 97e/97f fix). Hides the toggle
  // entirely instead of showing a half-broken expansion.
  if (eq?.raw_composite_score == null) return null;

  const raw = Number(eq.raw_composite_score) || 0;
  const forDelta = Number(eq.forensic_penalty_delta) || 0;
  const chkDelta = Number(eq.checklist_penalty_delta) || 0;
  const failed = Number(eq.checklist_failed_count) || 0;
  const afterForensic = raw + forDelta;
  const finalScore = afterForensic + chkDelta;
  const rank = eq.rank != null ? Number(eq.rank) : null;

  const fmt = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)} sd`;

  return (
    <div className="mt-3 pt-3 border-t border-border/40" data-testid="capital-rank-breakdown">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
        data-testid="capital-rank-breakdown-toggle"
        aria-expanded={open}
      >
        <span className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>▸</span>
        {open ? 'Hide Math' : 'Show Math — How did this rank?'}
      </button>
      {open && (
        <div className="mt-2 space-y-1 text-xs font-mono tabular-nums">
          {/* Round 97g (Feb 27, 2026) — Stacked penalty visualizer.
              Renders raw / forensic / checklist as a single 12-segment
              horizontal bar so non-quant users can SEE how much of
              their rank got clawed back. Width is computed as the
              fraction of (positive raw + |penalties|) so the bar
              always uses the full row. Only renders when there's
              actual content (raw>0 OR penalties exist). */}
          {(() => {
            const positives = Math.max(0, raw);
            const negatives = Math.abs(forDelta) + Math.abs(chkDelta);
            const total = positives + negatives;
            if (total <= 0.001) return null;
            const rawPct = (positives / total) * 100;
            const forPct = (Math.abs(forDelta) / total) * 100;
            const chkPct = (Math.abs(chkDelta) / total) * 100;
            return (
              <div className="mb-2" data-testid="capital-rank-breakdown-stack">
                <div className="flex w-full h-4 rounded-md overflow-hidden ring-1 ring-border/40">
                  {rawPct > 0 && (
                    <div
                      className="bg-emerald-500/80 dark:bg-emerald-500/80 flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ width: `${rawPct}%` }}
                      title={`Raw composite: ${raw.toFixed(2)} sd`}
                    >
                      {rawPct >= 12 && 'Raw'}
                    </div>
                  )}
                  {forPct > 0 && (
                    <div
                      className="bg-rose-600/80 dark:bg-rose-600/80 flex items-center justify-center text-[10px] font-bold text-white border-l border-background"
                      style={{ width: `${forPct}%` }}
                      title={`Forensic penalty: ${forDelta.toFixed(2)} sd`}
                    >
                      {forPct >= 12 && 'For'}
                    </div>
                  )}
                  {chkPct > 0 && (
                    <div
                      className="bg-rose-400/80 dark:bg-rose-400/80 flex items-center justify-center text-[10px] font-bold text-white border-l border-background"
                      style={{ width: `${chkPct}%` }}
                      title={`Checklist penalty: ${chkDelta.toFixed(2)} sd (${failed} of 5 failed)`}
                    >
                      {chkPct >= 12 && `Chk (${failed})`}
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground/70 mt-1">
                  <span className="text-emerald-700 dark:text-emerald-400">+ Raw {(rawPct).toFixed(0)}%</span>
                  <span className="text-rose-700 dark:text-rose-400">− Penalties {(forPct + chkPct).toFixed(0)}%</span>
                </div>
              </div>
            );
          })()}
          <div className="flex justify-between items-center py-1">
            <span className="text-muted-foreground">Raw composite z-score</span>
            <span className="font-bold">{fmt(raw)}</span>
          </div>
          <div className={`flex justify-between items-center py-1 ${forDelta < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}>
            <span>
              Forensic penalty
              {eq.forensic_penalty_applied ? (
                <span className="opacity-70 ml-1 text-[10px]">
                  (×{(0.20).toFixed(2)} — {eq.accounting_risk_flag ? 'Beneish flag' : ''}{eq.accounting_risk_flag && eq.distress_flag ? ' + ' : ''}{eq.distress_flag ? 'Altman distress' : ''})
                </span>
              ) : (
                <span className="opacity-70 ml-1 text-[10px]">(none — passed)</span>
              )}
            </span>
            <span className="font-bold">{fmt(forDelta)}</span>
          </div>
          <div className={`flex justify-between items-center py-1 ${chkDelta < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}>
            <span>
              Checklist penalty
              <span className="opacity-70 ml-1 text-[10px]">
                ({failed} of 5 failed × −0.35 sd)
              </span>
            </span>
            <span className="font-bold">{fmt(chkDelta)}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-t border-border/40 mt-1">
            <span className="font-bold uppercase text-[10px] tracking-wider text-muted-foreground">Final composite</span>
            <span className="font-bold">{fmt(finalScore)}</span>
          </div>
          {rank != null && (
            <div className="flex justify-between items-center py-1.5 bg-muted/30 -mx-1 px-1 rounded">
              <span className="font-bold uppercase text-[10px] tracking-wider">Percentile rank</span>
              <span className="font-bold text-base">{Math.round(rank)} / 100</span>
            </div>
          )}
          <div className="text-[10px] text-muted-foreground/80 mt-2 italic leading-relaxed">
            The raw composite blends Shareholder Yield, CFROI, Operating Momentum
            and Modified Altman Z (sector-neutralised z-scores). Penalties are
            subtracted in standard-deviation units before percentile ranking
            across the entire non-dividend universe.
          </div>
        </div>
      )}
    </div>
  );
}


function ChecklistRow({ label, pass, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm" data-testid={`capital-checklist-row-${label.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 30)}`}>
      <div className="flex items-center gap-2 flex-1">
        {pass ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0" />
        )}
        <span className="text-xs sm:text-sm">{label}</span>
      </div>
      <span className="text-xs font-semibold tabular-nums text-muted-foreground">{value}</span>
    </div>
  );
}


function MetricRow({ label, value, threshold, status, icon }) {
  const goodStatuses = ['Safe', 'CLEAN', 'EXPANDING', 'SHRINKING'];
  const isGood = goodStatuses.includes(status);
  const isBad = ['Distress', 'FLAG'].includes(status);
  return (
    <div className="grid grid-cols-12 items-center gap-2 py-1.5 text-xs sm:text-sm">
      <div className="col-span-5 text-muted-foreground">{label}</div>
      <div className="col-span-3 font-semibold tabular-nums">{value}</div>
      <div className="col-span-2 text-[10px] text-muted-foreground">{threshold}</div>
      <div className={`col-span-2 text-[10px] sm:text-xs font-semibold flex items-center gap-1 justify-end ${
        isGood ? 'text-emerald-600 dark:text-emerald-400' :
        isBad ? 'text-rose-600 dark:text-rose-400' :
        'text-amber-600 dark:text-amber-400'
      }`}>
        {icon}
        {status}
      </div>
    </div>
  );
}


function verdictCopy(verdict, eq) {
  if (verdict === 'BUY') {
    return (
      'Management is acting as a premier capital allocator. Retained cash flows are ' +
      'driving expanding operating efficiency, and the alternative return channel — net ' +
      'share-count reduction — is meaningfully accretive. Forensic and balance-sheet ' +
      'safeguards are intact.'
    );
  }
  if (verdict === 'WATCH') {
    if (eq.dilution_leak_trap_warning) {
      return (
        'The business is growing, but reported buyback spend is being absorbed by stock-' +
        'option grants rather than retiring float. ROIC or shareholder yield is lagging ' +
        'sector peers — watch the next two quarters for inflection.'
      );
    }
    return (
      'Reinvestment metrics are sector-typical but lack the explosive growth acceleration ' +
      'or aggressive net buybacks needed to drive outsized returns. Monitor for a clear ' +
      'directional move on momentum or shareholder yield.'
    );
  }
  return (
    'High forensic risk profile. The model has flagged Altman distress and/or a Beneish ' +
    'M-Score crossing above the manipulation threshold (-1.78). Significant downside ' +
    'risk from balance-sheet stress, dilution, or earnings-quality concerns.'
  );
}


function ThesisSection({ ticker, stockName }) {
  const [thesis, setThesis] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setGenerating(true);
    setErr(null);
    try {
      const res = await axios.post(`${API}/stocks/${ticker}/equity-quality/thesis`);
      setThesis(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to generate thesis');
    } finally {
      setGenerating(false);
    }
  };

  if (!thesis && !generating) {
    return (
      <Card className="p-4" data-testid="capital-thesis-section">
        <div className="text-center">
          <Sparkles className="w-8 h-8 mx-auto text-primary mb-2" />
          <div className="text-sm font-semibold mb-1">Write your thesis</div>
          <p className="text-xs text-muted-foreground mb-3">
            Generate a Bull vs Bear breakdown for {stockName || ticker} using
            the cached factor model. Powered by Gemini 3 Flash.
          </p>
          <Button onClick={generate} className="gap-2" data-testid="capital-thesis-generate-btn">
            <Sparkles className="w-4 h-4" /> Generate Bull vs Bear thesis
          </Button>
          {err && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{err}</div>}
        </div>
      </Card>
    );
  }

  if (generating) {
    return (
      <Card className="p-6 text-center" data-testid="capital-thesis-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary mb-2" />
        <div className="text-sm font-semibold mb-1">Generating thesis…</div>
        <p className="text-xs text-muted-foreground">This usually takes 5-15 seconds.</p>
      </Card>
    );
  }

  return (
    <Card className="p-4 space-y-4" data-testid="capital-thesis-rendered">
      <div>
        <SectionHeader title="AI Thesis" subtitle="Bull vs Bear breakdown" />
        {thesis.headline && (
          <p className="text-sm font-medium mb-3">{thesis.headline}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold mb-2">
            Bull Case
          </div>
          <ul className="space-y-1.5 text-xs">
            {(thesis.bull_case || []).map((b, i) => (
              <li key={i}>• {b}</li>
            ))}
          </ul>
        </div>
        <div className="p-3 rounded-md bg-rose-500/5 border border-rose-500/20">
          <div className="text-[11px] uppercase tracking-wider text-rose-700 dark:text-rose-400 font-semibold mb-2">
            Bear Case
          </div>
          <ul className="space-y-1.5 text-xs">
            {(thesis.bear_case || []).map((b, i) => (
              <li key={i}>• {b}</li>
            ))}
          </ul>
        </div>
      </div>

      {thesis.synthesis && (
        <div className="p-3 rounded-md bg-muted/30">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Synthesis</div>
          <p className="text-xs leading-relaxed">{thesis.synthesis}</p>
        </div>
      )}

      {thesis.what_changes_our_mind?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            What would change this view
          </div>
          <ul className="text-xs space-y-1 text-muted-foreground">
            {thesis.what_changes_our_mind.map((m, i) => (
              <li key={i}>• {m}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[10px] text-muted-foreground italic">
        {thesis.data_source === 'fallback'
          ? 'LLM unavailable — showing deterministic fallback. Try again later.'
          : 'Generated by Gemini 3 Flash. Cached for 7 days. Not financial advice.'}
      </div>
    </Card>
  );
}


function WowDeltaPill({ currentRank, previousRank }) {
  // Round 97i (Feb 28, 2026) — Week-over-week delta arrow on the
  // Capital Reinvestment Score badge. `previous_rank` is persisted
  // by the weekly EQR cron just before it overwrites the new rank.
  // First-run tickers have previous_rank = null → render nothing.
  if (currentRank == null || previousRank == null) return null;
  const delta = Number(currentRank) - Number(previousRank);
  if (!Number.isFinite(delta) || Math.abs(delta) < 0.5) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-muted-foreground px-1.5 py-0.5 rounded-full bg-muted/50"
        title={`Unchanged from last week (${Number(previousRank).toFixed(1)} → ${Number(currentRank).toFixed(1)})`}
        data-testid="capital-score-wow-delta"
      >
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const positive = delta > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
        positive
          ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/15'
          : 'text-rose-700 dark:text-rose-400 bg-rose-500/15'
      }`}
      title={`Week-over-week delta: ${Number(previousRank).toFixed(1)} → ${Number(currentRank).toFixed(1)}`}
      data-testid="capital-score-wow-delta"
    >
      <Icon className="w-3 h-3" /> {positive ? '+' : ''}{delta.toFixed(1)}
    </span>
  );
}


function BuybackEffectivenessCard({ ticker }) {
  // Round 97i (Feb 28, 2026) — On-demand "Buyback Effectiveness" score.
  // Lazy-loads when the tab mounts; cached server-side for 7 days.
  // Surfaces the same dilution-leak signal the AI thesis flags, but
  // as a hard percent number so users can compare across tickers.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/stocks/${ticker}/buyback-effectiveness`)
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) {
    return (
      <Card className="p-4" data-testid="capital-buyback-effectiveness-loading">
        <SectionHeader title="Buyback Effectiveness" subtitle="Dilution-adjusted spend ratio" />
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pulling cashflow data…
        </div>
      </Card>
    );
  }

  if (!data?.available) {
    const reason = data?.reason;
    let copy = 'Buyback effectiveness can\'t be computed — no annual repurchase data available for this ticker.';
    if (reason === 'equity_quality_not_scored') {
      copy = 'Waiting on the next equity-quality cron run before effectiveness can be calculated.';
    } else if (reason === 'no_buyback_data') {
      copy = 'Company has no reported share-repurchase activity in the latest annual cashflow filing.';
    }
    return (
      <Card className="p-4" data-testid="capital-buyback-effectiveness-unavailable">
        <SectionHeader title="Buyback Effectiveness" subtitle="Dilution-adjusted spend ratio" />
        <p className="text-xs text-muted-foreground py-2">{copy}</p>
      </Card>
    );
  }

  const eff = Number(data.effectiveness_pct);
  // Clamp the displayed bar to [-25, 125] so a -300% outlier doesn't
  // crush the rest of the scale. The raw % still shows in the headline.
  const barPct = Math.max(0, Math.min(100, ((eff - (-25)) / (125 - (-25))) * 100));
  const tierBg = data.color === 'green' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
    : data.color === 'amber' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'
    : data.color === 'red' ? 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30'
    : 'bg-muted/50 text-muted-foreground border-border';
  const pinColor = data.color === 'green' ? 'bg-emerald-500'
    : data.color === 'amber' ? 'bg-amber-500'
    : data.color === 'red' ? 'bg-rose-500'
    : 'bg-muted-foreground';

  const fmtDollars = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    const abs = Math.abs(v);
    if (abs >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    return `${v.toFixed(0)}`;
  };
  const wasted = (data.gross_buyback_dollars || 0) - (data.net_float_reduction_dollars || 0);

  return (
    <Card className="p-4" data-testid="capital-buyback-effectiveness-card">
      <SectionHeader
        title={<AbbrTip term="BUYBACK_EFFECTIVENESS">Buyback Effectiveness</AbbrTip>}
        subtitle="Float reduction value ÷ gross buyback spend"
      />
      <div className="flex items-baseline gap-3 mt-1">
        <div className="text-3xl font-bold tabular-nums" data-testid="capital-buyback-effectiveness-value">
          {eff.toFixed(0)}%
        </div>
        <span
          className={`text-[11px] uppercase tracking-wider font-bold px-2 py-1 rounded border ${tierBg}`}
          data-testid={`capital-buyback-effectiveness-tier-${data.tier}`}
        >
          {data.label}
        </span>
      </div>

      {/* Visual scale: 0% red ─ 25% amber ─ 50% green ─ 75% elite green */}
      <div className="mt-3 relative h-2 rounded-full overflow-hidden bg-muted/40" data-testid="capital-buyback-effectiveness-bar">
        <div className="absolute top-0 left-0 h-full w-[17%] bg-rose-500/35 dark:bg-rose-600/40" />
        <div className="absolute top-0 left-[17%] h-full w-[33%] bg-amber-500/35 dark:bg-amber-600/40" />
        <div className="absolute top-0 left-[50%] h-full w-[50%] bg-emerald-500/35 dark:bg-emerald-600/40" />
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-2 ring-background shadow ${pinColor}`}
          style={{ left: `calc(${barPct}% - 5px)` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground/70 mt-0.5 font-mono">
        <span>-25%</span>
        <span className="opacity-90">25% → mixed | 50% → effective</span>
        <span>125%</span>
      </div>

      {/* Plain-english breakdown so non-quant users understand. */}
      <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gross spend</div>
          <div className="text-sm font-semibold tabular-nums" data-testid="capital-buyback-gross-spend">
            ${fmtDollars(data.gross_buyback_dollars)}
          </div>
          <div className="text-[9px] text-muted-foreground/80 mt-0.5 leading-tight">cash paid to repurchase shares</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Float retired</div>
          <div
            className={`text-sm font-semibold tabular-nums ${
              (data.net_float_reduction_dollars || 0) < 0
                ? 'text-rose-600 dark:text-rose-400'
                : ''
            }`}
            data-testid="capital-buyback-float-retired"
          >
            ${fmtDollars(data.net_float_reduction_dollars)}
          </div>
          <div className="text-[9px] text-muted-foreground/80 mt-0.5 leading-tight">
            {(data.net_float_reduction_dollars || 0) < 0
              ? 'float GREW — net dilution'
              : 'real value of shares retired'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Absorbed</div>
          <div
            className={`text-sm font-semibold tabular-nums ${
              wasted > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'
            }`}
            data-testid="capital-buyback-absorbed"
          >
            ${fmtDollars(Math.max(0, wasted))}
          </div>
          <div className="text-[9px] text-muted-foreground/80 mt-0.5 leading-tight">went to option grants &amp; SBC</div>
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
        For every dollar management spent buying back shares in FY{data.fiscal_year ? ` ${String(data.fiscal_year).slice(0, 4)}` : ''},
        {' '}<span className="font-semibold text-foreground">{Math.max(0, Math.min(100, eff)).toFixed(0)}¢</span> actually
        retired float. The remainder was absorbed by option grants and stock-based-compensation (SBC) issuances.
        {eff < 0 && (
          <> This is a <span className="font-semibold text-rose-600 dark:text-rose-400">net-dilution scenario</span> — the company spent real cash on buybacks AND issued more new shares than it bought back. Total shareholder value lost to dilution exceeds the entire buyback program. A textbook signal that management compensation is being silently funded by existing shareholders.</>
        )}
        {eff >= 0 && eff < 25 && ' This is a textbook "dilution leak" — buybacks are masking, not offsetting, employee equity grants. Watch the next two quarters to see if management throttles SBC or accelerates repurchases.'}
        {eff >= 25 && eff < 50 && ' A meaningful share of buyback cash is being absorbed by new issuance. Track this trend quarter-over-quarter — a falling effectiveness number is an early warning that comp packages are outpacing capital returns.'}
        {eff >= 50 && eff < 75 && ' Buybacks are mostly effective. Modest dilution from option grants is expected at scale and isn\'t a red flag on its own — what matters is the trend over time.'}
        {eff >= 75 && ' Highly effective — management is retiring real float, not just running the buyback program to neutralize compensation expense. Each dollar spent meaningfully concentrates your ownership.'}
      </p>

      {/* Round 97i+ (Feb 28, 2026) — Explicit "how to read this" footer
          so non-quant users understand the math without leaving the tab. */}
      <div className="mt-3 pt-3 border-t border-border/40 text-[10px] text-muted-foreground/90 leading-relaxed space-y-1">
        <div><span className="font-semibold text-foreground">How we measure this:</span> Float Retired is the dollar value of the share-count change YoY × the 5-day VWAP price. Subtract that from Gross Spend and you get Absorbed — the value that didn&apos;t shrink your ownership stake.</div>
        <div><span className="font-semibold text-foreground">Why it matters:</span> A high number means each buyback dollar makes your existing shares a larger slice of the company. A low (or negative) number means management is using buybacks to mask employee compensation expense rather than return capital to you.</div>
      </div>
    </Card>
  );
}


function RuleOf40Tile({ label, score, growthPct, marginPct, marginLabel, testid }) {
  // Round 97k (Feb 28, 2026) — Single Rule-of-40 tile (FCF or Op variant).
  // Renders gracefully when score / margin is null so missing data never
  // breaks the parent card.
  const available = score != null && Number.isFinite(score);
  const tier = !available ? 'unknown'
    : score >= 60 ? 'elite'
    : score >= 40 ? 'pass'
    : score >= 30 ? 'borderline'
    : score >= 0 ? 'fail'
    : 'negative';
  const tierMeta = {
    elite:      { text: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', pin: 'bg-emerald-500', label: 'Elite' },
    pass:       { text: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', pin: 'bg-emerald-500', label: 'Healthy' },
    borderline: { text: 'text-amber-700 dark:text-amber-400',     bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   pin: 'bg-amber-500',   label: 'Borderline' },
    fail:       { text: 'text-rose-700 dark:text-rose-400',       bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    pin: 'bg-rose-500',    label: 'Failing' },
    negative:   { text: 'text-rose-700 dark:text-rose-400',       bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    pin: 'bg-rose-500',    label: 'Burning cash' },
    unknown:    { text: 'text-muted-foreground',                  bg: 'bg-muted/30',       border: 'border-border',         pin: 'bg-muted-foreground', label: '—' },
  }[tier];
  // Scale: clamp -25..125 like the buyback tile so an outlier doesn't crush
  // the rest of the bar.
  const barPct = available ? Math.max(0, Math.min(100, ((score - (-25)) / (125 - (-25))) * 100)) : 50;

  return (
    <div className={`rounded-lg border ${tierMeta.border} ${tierMeta.bg} p-3`} data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${tierMeta.text}`}>
          {tierMeta.label}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <div className="text-2xl font-bold tabular-nums" data-testid={`${testid}-value`}>
          {available ? score.toFixed(0) : '—'}
        </div>
        {available && (
          <div className="text-xs text-muted-foreground">target ≥ 40</div>
        )}
      </div>

      {/* Threshold bar with the 40 line marked. */}
      <div className="mt-2 relative h-1.5 rounded-full overflow-hidden bg-muted/40">
        <div className="absolute top-0 left-0 h-full w-[17%] bg-rose-500/30 dark:bg-rose-600/35" />
        <div className="absolute top-0 left-[17%] h-full w-[27%] bg-amber-500/30 dark:bg-amber-600/35" />
        <div className="absolute top-0 left-[44%] h-full w-[56%] bg-emerald-500/30 dark:bg-emerald-600/35" />
        {/* The "40 line" sits at (40 - (-25)) / 150 = 43.33% on this scale. */}
        <div className="absolute top-0 left-[43.33%] h-full w-px bg-foreground/50" />
        {available && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ring-2 ring-background ${tierMeta.pin}`}
            style={{ left: `calc(${barPct}% - 4px)` }}
          />
        )}
      </div>

      {/* Component breakdown — only shown when we have the underlying numbers. */}
      {available && (growthPct != null || marginPct != null) && (
        <div className="mt-2 text-[10px] text-muted-foreground font-mono tabular-nums">
          {growthPct != null ? `${growthPct.toFixed(1)}%` : '—'} growth + {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'} {marginLabel}
        </div>
      )}
    </div>
  );
}


function RuleOf40Card({ eq }) {
  // Round 97k (Feb 28, 2026) — Standalone Rule of 40 card. Does NOT
  // feed the Capital Reinvestment Score (Revenue CAGR + Operating
  // Momentum already do that); displayed independently. Renders the
  // FCF variant and Operating variant side by side. Gracefully shows
  // a "data not yet computed" stub when the weekly EQR cron has not
  // populated these fields for this ticker yet.
  const hasAny = (eq?.rule_of_40_fcf_raw != null) || (eq?.rule_of_40_op_raw != null) || (eq?.rule_of_40_sbc_raw != null);

  if (!hasAny) {
    const nm = eq?.rev_growth_nm_reason;
    return (
      <Card className="p-4" data-testid={nm ? 'capital-rule-of-40-nm' : 'capital-rule-of-40-unavailable'}>
        <SectionHeader
          title={<AbbrTip term="RULE_OF_40">Rule of 40</AbbrTip>}
          subtitle="Revenue growth + margin efficiency"
        />
        {nm ? (
          <p className="text-xs text-muted-foreground py-2" data-testid="capital-rule-of-40-nm-reason">
            <span className="font-semibold text-amber-700 dark:text-amber-400">Not meaningful</span> — {nm}. A growth-plus-margin score built on this base would read in the hundreds or thousands of percent and tell you nothing, so we suppress it instead of printing a misleading number.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground py-2">
            Rule of 40 will appear here after the next weekly equity-quality refresh. The metric needs at least two years of revenue history plus the most recent annual cashflow filing.
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card className="p-4" data-testid="capital-rule-of-40-card">
      <SectionHeader
        title={<AbbrTip term="RULE_OF_40">Rule of 40</AbbrTip>}
        subtitle="Revenue growth + margin efficiency"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
        <RuleOf40Tile
          label="Rule of 40 — FCF"
          score={eq.rule_of_40_fcf_raw}
          growthPct={eq.rev_growth_yoy_pct}
          marginPct={eq.fcf_margin_pct}
          marginLabel="FCF margin"
          testid="capital-rule-of-40-fcf"
        />
        <RuleOf40Tile
          label="Rule of 40 — Operating"
          score={eq.rule_of_40_op_raw}
          growthPct={eq.rev_growth_yoy_pct}
          marginPct={eq.op_margin_pct}
          marginLabel="op margin"
          testid="capital-rule-of-40-op"
        />
        {eq.rule_of_40_sbc_raw != null && (
          <div className="sm:col-span-2" title={`SBC-adjusted: Free Cash Flow minus Stock-Based Compensation, ÷ Revenue. SBC was ${eq.sbc_pct_of_revenue != null ? eq.sbc_pct_of_revenue.toFixed(1) : '—'}% of revenue this year (source: SEC XBRL us-gaap:ShareBasedCompensation, annual 10-K). GAAP adds SBC back inside Cash from Operations because no cash leaves the building — but the shares do. If the standard FCF line passes and this one fails, the business is funding its operations by diluting you.`}>
            <RuleOf40Tile
              label="Rule of 40 — SBC-adjusted FCF"
              score={eq.rule_of_40_sbc_raw}
              growthPct={eq.rev_growth_yoy_pct}
              marginPct={eq.sbc_adj_fcf_margin_pct}
              marginLabel="FCF − SBC margin"
              testid="capital-rule-of-40-sbc"
            />
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-border/40 text-[10px] text-muted-foreground/90 leading-relaxed space-y-1">
        <div>
          <span className="font-semibold text-foreground">How we measure this:</span>{' '}
          Rule of 40 = most recent year-over-year revenue growth (%) plus profit margin (%). The FCF variant uses Free Cash Flow ÷ Revenue (the Bessemer / SaaS-investor convention). The Operating variant uses Operating Income ÷ Revenue (more generous for capex-heavy businesses).
        </div>
        <div>
          <span className="font-semibold text-foreground">Why it matters:</span>{' '}
          A reading at or above 40 means the business is scaling efficiently — every percentage point of growth comes with enough profitability that it doesn&apos;t need to burn capital to grow. Below 30, the company is buying growth with money it isn&apos;t making. A FCF-variant pass paired with an Operating-variant fail can flag heavy stock-based compensation diluting reported profit.
        </div>
        <div>
          <span className="font-semibold text-foreground">Two blind spots:</span>{' '}
          <span title="Unlike SaaS (customers prepay annual subscriptions), asset-light non-software compounders can see inventory or receivables swing sharply. A one-quarter working-capital build can crater trailing FCF margin and make a great business fail the test temporarily — check the Operating variant and the cash-flow statement before judging.">
            <strong>Working-capital trap</strong> — a temporary inventory / receivables build can crater the FCF margin for a year;
          </span>{' '}
          <span title="Stock-based compensation is a real employee expense paid in shares. GAAP subtracts it from Net Income, then adds it straight back inside Cash from Operations (it's non-cash), so standard FCF looks inflated. The SBC-adjusted line subtracts it again using the SEC-filed figure, so you see what the business earns after paying its people.">
            <strong>SBC add-back</strong> — stock comp is added back into operating cash flow, so standard FCF flatters heavy-SBC companies. The SBC-adjusted tile{eq.rule_of_40_sbc_raw != null ? '' : ' (shown when the SEC filing carries the figure)'} removes it.
          </span>
        </div>
        <div className="text-muted-foreground/70 italic">Standalone metric. Does not feed the Capital Reinvestment Score. Suppressed (n/m) when either year&apos;s revenue is under $5M or revenue jumped more than 4× year-over-year.</div>
      </div>
    </Card>
  );
}

